import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Handshake, Store } from "lucide-react";
import { Button } from "@veorest/ui";
import {
  fetchAdminBillingConfigs,
  updateAdminBillingConfig,
  type AdminBillingConfig,
  type AdminBillingConfigCode,
} from "@veorest/api-client";
import { formatDate, formatIqd } from "../../lib/format";

const CONFIG_META: Record<
  AdminBillingConfigCode,
  { icon: typeof Store; payer: string; applies: string }
> = {
  direct_client: {
    icon: Store,
    payer: "Client wallet",
    applies:
      "Charged to OELOX direct clients — debited from the client wallet when a driver-handled trip completes.",
  },
  partner: {
    icon: Handshake,
    payer: "Partner wallet (consolidated)",
    applies:
      "Charged to logistics partners — debited from the consolidated partner wallet. The partner bills his own clients with his own delivery fees, not with this plan.",
  },
};

function PlanCard({
  plan,
  onSaved,
}: {
  plan: AdminBillingConfig;
  onSaved: () => void;
}) {
  const [fee, setFee] = useState(() => String(plan.per_trip_fee_iqd));

  useEffect(() => {
    setFee(String(plan.per_trip_fee_iqd));
  }, [plan.code, plan.per_trip_fee_iqd]);

  const meta = CONFIG_META[plan.code];
  const Icon = meta.icon;

  const save = useMutation({
    mutationFn: () => {
      const value = Number(fee);
      if (fee.trim() === "" || !Number.isFinite(value) || value < 0) {
        throw new Error("Enter a valid amount (0 or more IQD per trip).");
      }
      return updateAdminBillingConfig(plan.code, value);
    },
    onSuccess: (updated) => {
      setFee(String(updated.per_trip_fee_iqd));
      toast.success(`${plan.name} — trip fee updated`);
      onSaved();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dirty = fee.trim() !== "" && Number(fee) !== plan.per_trip_fee_iqd;

  return (
    <section className="overview-panel mt-4">
      <header className="overview-panel__header">
        <span className="overview-panel__icon" aria-hidden>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="overview-panel__title">{plan.name}</h3>
          <p className="overview-panel__subtitle">{meta.payer}</p>
        </div>
        <span className="rounded-full bg-[var(--surface-muted)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
          {plan.code}
        </span>
      </header>
      <div className="overview-panel__body space-y-4 p-5">
        <p className="text-sm font-semibold text-[var(--text-secondary)]">{meta.applies}</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block space-y-1">
            <span className="text-xs font-bold uppercase text-[var(--text-muted)]">
              IQD per completed trip
            </span>
            <input
              className="input-base w-40"
              inputMode="decimal"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
            />
          </label>
          <Button
            type="button"
            className="min-h-[40px]"
            loading={save.isPending}
            disabled={!dirty}
            onClick={() => save.mutate()}
          >
            Save trip fee
          </Button>
          <span className="pb-2 text-xs font-semibold text-[var(--text-muted)]">
            Current: {formatIqd(plan.per_trip_fee_iqd)} per trip
          </span>
        </div>
        {plan.updated_at ? (
          <p className="text-xs font-semibold text-[var(--text-muted)]">
            Last change {formatDate(plan.updated_at)}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/**
 * Plans Settings (migration 181): OELOX bills exactly two payers per completed
 * driver-handled trip — direct clients (client wallet) and partners (consolidated
 * partner wallet). Customer self-pickup / dine-in orders without a driver are
 * never charged.
 */
export function PlansSettingsPanel() {
  const qc = useQueryClient();
  const configs = useQuery({
    queryKey: ["admin-billing-configs"],
    queryFn: fetchAdminBillingConfigs,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["admin-billing-configs"] });

  return (
    <div className="space-y-2">
      <section className="overview-panel mt-4">
        <div className="overview-panel__body p-5">
          <h3 className="overview-panel__title mb-1">Plans Settings</h3>
          <p className="text-sm text-[var(--text-muted)] font-semibold leading-relaxed">
            Trip billing configuration — two payers, both charged <strong>per completed trip
            handled by a driver</strong>: <strong>Direct Clients to OELOX</strong> (client wallet)
            and <strong>Partner</strong> (consolidated partner wallet). Customer self-pickup /
            dine-in orders without a driver are never charged, and any fallback fee use is
            logged — never silent.
          </p>
        </div>
      </section>

      {configs.isLoading ? (
        <p className="mt-4 text-sm font-semibold text-[var(--text-muted)]">Loading billing configs…</p>
      ) : configs.isError ? (
        <p className="mt-4 text-sm font-semibold text-red-500">
          Could not load billing configs: {(configs.error as Error)?.message}
        </p>
      ) : (
        (configs.data?.configs ?? []).map((plan) => (
          <PlanCard key={plan.code} plan={plan} onSaved={invalidate} />
        ))
      )}

      <p className="mt-4 text-xs font-semibold text-[var(--text-muted)]">
        A saved fee applies to trips completed after saving — no retro-charging. Delivery fees the
        partner charges his own clients remain the partner's own settings (Delivery tab → partner
        trip pricing / partner dashboard).
      </p>
    </div>
  );
}
